import { z } from 'zod';

// Base Schemas
export const DegreeSchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  slug: z.string().optional(),
});

export const CategorySchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  slug: z.string().optional(),
});

export const SubjectSchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  slug: z.string().optional(),
});

export const DestinationSchema = z.object({
  url: z.string(),
  helid: z.string().optional(),
});

export const LocationRestrictionSchema = z.object({
  allowedIn: z.array(z.string()),
  blockedIn: z.array(z.string()),
});

export const OrganizationMetaSchema = z.object({
  sdbSchoolId: z.string().optional(),
});

export const OrganizationSchema = z.object({
  id: z.string(),
  source: z.string(),
  sourceId: z.number(),
  name: z.string(),
  slug: z.string(),
  description: z.string().optional(),
  meta: OrganizationMetaSchema.optional(),
});

// Application CTA Schema
export const ApplicationCtaSchema = z.object({
  label: z.string(),
  url: z.string(),
});

// Offering Meta Schema
export const OfferingMetaSchema = z.object({
  applicationCta: ApplicationCtaSchema,
  providerId: z.number(),
  providerName: z.string(),
  sdbProgramId: z.string(),
  preferred: z.boolean(),
  degree: DegreeSchema,
  category: CategorySchema,
  subject: SubjectSchema,
  bonsaiERPIState: z.record(z.string(), z.number()),
  audience: z.string().optional(),
  capId: z.number(),
});

// Main Offering Schema
export const OfferingSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  source: z.string(),
  type: z.string(),
  subType: z.string(),
  sourceUrl: z.string(),
  slug: z.string(),
  description: z.string(),
  shortDescription: z.string().optional(),
  overview: z.string().optional(),
  snippet: z.string(),
  attributes: z.array(z.string()).optional(),
  occupationCodes: z.array(z.string()).optional(),
  imageUrl: z.string().optional(),
  destination: DestinationSchema,
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  hash: z.string().optional(),
  locationRestrictions: z.array(LocationRestrictionSchema).optional(),
  organization: OrganizationSchema,
  subjects: z.array(z.string()).optional(),
  skills: z.array(z.string()).optional(),
  level: z.string().optional(),
  flexibility: z.string().optional(),
  weeksToComplete: z.number().optional(),
  weeksToCompleteMin: z.number().optional(),
  weeksToCompleteMax: z.number().optional(),
  minHoursEffortPerWeek: z.number().optional(),
  courseCount: z.number().nullable().optional(),
  position: z.number().optional(),
  meta: OfferingMetaSchema,
});

// Response Data Schema
export const OfferingResponseDataSchema = z.object({
  exact: z.array(OfferingSchema),
  related: z.array(OfferingSchema),
  recommended: z.array(OfferingSchema),
  spotlight: z.array(OfferingSchema),
});

// Main Response Schema
export const OfferingResponseSchema = z.object({
  data: OfferingResponseDataSchema,
  tracking: z
    .object({
      sortingReferenceId: z.string(),
    })
    .optional(),
});

// Exact Offering Response Schema
export const ExactOfferingResponseSchema = z.object({
  data: z.array(OfferingSchema),
});

// Pagination Schema
export const PaginationSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

// Search Response Schema
export const OfferingsSearchResponseSchema = z.object({
  data: z.array(OfferingSchema),
  pagination: PaginationSchema,
});

// Force Audience Schema
export const ForceAudienceSchema = z.object({
  audience: z.string(),
  schoolIds: z.array(z.number()),
});

// Offering Payload Schema (for requests)
export const OfferingPayloadSchema = z.object({
  degree: z.string().optional(),
  category: z.string().optional(),
  subject: z.string().optional(),
  unqualified: z.array(z.string()).optional(),
  qualified: z.array(z.string()).optional(),
  startDate: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  channel: z.string().optional(),
  media: z.string().optional(),
  experience: z.string().optional(),
  presentationUrl: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  programIds: z.array(z.number()).optional(),
  excludeProgramIds: z.array(z.number()).optional(),
  schoolIds: z.array(z.number()).optional(),
  providers: z.array(z.string()).optional(),
  excludeProviders: z.array(z.string()).optional(),
  forceAudience: ForceAudienceSchema.optional(),
});

// Ranking Offerings Payload Schema — POST /v1/offerings/ranking
// Matches the contract locked in EDUN-10969 (see Jira comment "Contract:
// POST /v1/offerings/ranking"). `excludeSchoolIds` is accepted today but
// is currently a no-op on the backend; the client is expected to filter
// school IDs out of the response for MVP (tracked by EDUN-11147).
export const RankingOfferingsPayloadSchema = z.object({
  degree: z.string().optional(),
  category: z.string().optional(),
  subject: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  channel: z.string().optional(),
  experience: z.string().optional(),
  presentationUrl: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  excludeSchoolIds: z.array(z.number()).optional(),
  excludeProgramIds: z.array(z.number()).optional(),
  excludeProviders: z.array(z.string()).optional(),
});

// Ranking Offerings Response Schema — envelope matches qualified offerings.
export const RankingOfferingsResponseSchema = z.object({
  data: z.array(OfferingSchema),
  tracking: z
    .object({
      sortingReferenceId: z.string().optional(),
    })
    .optional(),
});

// Search Payload Schema
export const OfferingsSearchPayloadSchema = z.object({
  query: z.string().optional(),
  subjects: z.array(z.string()).optional(),
  skills: z.array(z.string()).optional(),
  level: z.string().optional(),
  flexibility: z.string().optional(),
  type: z.string().optional(),
  subType: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  uuids: z.array(z.string()).optional(),
  excludeProviders: z.array(z.string()).optional(),
});

// Filters Schema
export const FiltersSchema = z.object({
  types: z.record(z.string(), z.number()),
  subjects: z.record(z.string(), z.number()),
  subTypes: z.record(z.string(), z.number()),
  skills: z.record(z.string(), z.number()).optional(),
});

// Question Option Schema
export const QuestionOptionSchema = z.object({
  optionId: z.string(),
  text: z.string(),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
  leadFieldAssociated: z.string().optional(),
});

// Question Schema
export const QuestionSchema = z.object({
  options: z.array(QuestionOptionSchema),
  questionType: z.literal('SELECT'),
  id: z.string(),
  type: z.literal('QUESTION'),
  title: z.string().optional(),
});

// Degree Fields Config Schema
export const DegreeFieldsConfigSchema = z.record(
  z.string(),
  z.object({
    question: QuestionSchema,
  }),
);

// Recommendations Response Schema
export const RecommendationsResponseSchema = z.record(
  z.string(),
  z.object({
    courses: z.array(OfferingSchema),
  }),
);

// Dimensions (taxonomy) Schemas — GET /v1/offerings/dimensions
// Each node carries a canonical `key` (slug) and human `label`. The live
// payload includes additional per-node fields (counts, children, etc.), so
// node/dimension objects are kept permissive (`.passthrough()`) — only `key`
// and `label` are consumed (slug→label resolution).
export const DimensionNodeSchema = z
  .object({
    key: z.string().optional(),
    label: z.string().optional(),
  })
  .passthrough();

export const DimensionSchema = z
  .object({
    nodes: z.array(DimensionNodeSchema).optional(),
  })
  .passthrough();

export const DimensionsResponseSchema = z.object({
  dimensions: z.record(z.string(), DimensionSchema).optional(),
});

// Type exports for use in TypeScript
export type Offering = z.infer<typeof OfferingSchema>;
export type OfferingResponse = z.infer<typeof OfferingResponseSchema>;
export type ExactOfferingResponse = z.infer<typeof ExactOfferingResponseSchema>;
export type Pagination = z.infer<typeof PaginationSchema>;
export type OfferingsSearchResponse = z.infer<
  typeof OfferingsSearchResponseSchema
>;
export type OfferingPayload = z.infer<typeof OfferingPayloadSchema>;
export type OfferingsSearchPayload = z.infer<
  typeof OfferingsSearchPayloadSchema
>;
export type RankingOfferingsPayload = z.infer<
  typeof RankingOfferingsPayloadSchema
>;
export type RankingOfferingsResponse = z.infer<
  typeof RankingOfferingsResponseSchema
>;
export type Filters = z.infer<typeof FiltersSchema>;
export type Question = z.infer<typeof QuestionSchema>;
export type DegreeFieldsConfig = z.infer<typeof DegreeFieldsConfigSchema>;
export type RecommendationsResponse = z.infer<
  typeof RecommendationsResponseSchema
>;
export type DimensionNode = z.infer<typeof DimensionNodeSchema>;
export type Dimension = z.infer<typeof DimensionSchema>;
export type DimensionsResponse = z.infer<typeof DimensionsResponseSchema>;
